import { Trans, useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import React, { useMemo, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";

import i18next from "i18next";
import { formatCurrency } from "../../../utils";
import useTemplate from "../../../hooks/useTemplate";
import { getTemplateTranslation } from "../../../Providers/TemplateInstanceProvider";
import {
  PRODUCT_CATALOGUE_UUID,
  PRODUCT_DIGITAL_SET_UUID,
  REBATE_CODE_UUID,
  usePriceList,
} from "../../providers/PriceListProvider";
import VoucherCodeDialog from "./VoucherCodeDialog";

const useStyles = makeStyles((theme) => ({
  orderLineDescription: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    flex: "1 1 100%",
    fontWeight: "500",
    fontSize: "14px",
  },
  orderLineSubHeader: {
    fontSize: "12px",
    marginBottom: theme.spacing(2),
  },
  orderLinePrice: {
    flex: "0 0 auto",
    textAlign: "right",
    fontWeight: "bold",
  },
  priceBeforeRebate: {
    flex: "0 0 auto",
    textAlign: "right",
    color: "#A5A5A5",
    textDecoration: "line-through",
  },
  orderContentList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    borderBottom: `1px solid ${theme.palette.primary.light}`,
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    "& > li": {
      marginBottom: theme.spacing(2),
    },
    "& > li:last-child": {
      marginBottom: 0,
    },
  },
  orderTotalPriceList: {
    paddingBottom: "2px",
  },
  orderLinePrimary: {
    fontSize: "15px",
    fontWeight: "bold",
  },
  orderLineSecondary: {
    fontStyle: "italic",
    fontSize: "13px",
  },
  packageProductList: {
    paddingTop: theme.spacing(1),
    paddingLeft: "20px",
    listStyle: "none",
    "& > li > ul": {
      paddingLeft: "20px",
      listStyle: "disc",
      marginBottom: theme.spacing(1),
      lineHeight: "1.2em",
    },
  },
  packageTemplateName: {
    fontSize: "12px",
  },
  packageProductName: {
    marginBottom: "4px",
    lineHeight: "1.05em",
  },

  packagePartListPrimary: {
    fontSize: "15px",
  },
  packagePartListSecondary: {
    fontSize: "13px",
    fontStyle: "italic",
  },
  subProductName: {
    fontSize: "12px",
    fontWeight: 500,
    fontStyle: "normal",
  },
  orderTotalPrice: {
    fontSize: "20px",
    fontWeight: "bold",
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.primary.light}`,
  },
  priceToPay: {
    fontWeight: "bold",
  },
  addCodeButton: {
    color: theme.palette.primary.main,
    border: 0,
    backgroundColor: "transparent",
    cursor: "pointer",
    fontSize: "15px",
  },
  oldPrice: {
    textDecoration: "line-through",
    color: "#A5A5A5",
    fontWeight: "normal",
  },
}));

const OrderContent = ({
  orderLines,
  hasActiveRebateCampaigns,
  rebateCode,
}) => {
  const classes = useStyles();

  // Order line type is 0 and not invoice:
  const productList = orderLines.filter(
    (ol) =>
      ol.type == 0 &&
      ol.product?.uuid != "a0957c46-ed22-426a-8263-28e47541dfad",
  );

  const summaryList = orderLines.filter(
    (ol) =>
      ol.type == 3 ||
      ol.product?.uuid == "a0957c46-ed22-426a-8263-28e47541dfad",
  );

  const totalPriceLine = orderLines.filter((ol) => ol.type == 4)[0];
  console.log("Order line", orderLines);

  return (
    <>
      <ul className={classes.orderContentList}>
        {productList.map((orderLine) => {
          return (
            <li key={orderLine.uuid}>
              {orderLine.product.type == 200 ? (
                <PackageInCart orderLine={orderLine} />
              ) : (
                <ProductInCart orderLine={orderLine} />
              )}
            </li>
          );
        })}
      </ul>
      {summaryList.length > 0 && (
        <ul className={classes.orderContentList}>
          {summaryList.map((orderLine, index) => {
            return (
              <li key={orderLine.uuid + index}>
                <DisplayOrderSummary orderLine={orderLine} />
              </li>
            );
          })}
        </ul>
      )}
      {hasActiveRebateCampaigns && (
        <VoucherCodeSection orderLines={orderLines} rebateCode={rebateCode} />
      )}

      <ul
        className={`${classes.orderContentList} ${classes.orderTotalPriceList}`}
      >
        <li className={classes.orderTotalPrice}>
          <Box display="flex" justifyContent="space-between">
            <span>{totalPriceLine.description}</span>
            <span>{formatCurrency(totalPriceLine.linePrice)}</span>
          </Box>
        </li>
      </ul>
    </>
  );
};

const VoucherCodeSection = ({ orderLines, rebateCode }) => {
  const rebateLines = orderLines.filter(
    (ol) =>
      ol.type == 2 || (ol.type == 1 && ol.product?.uuid === REBATE_CODE_UUID),
  );
  const classes = useStyles();
  const [voucherCodeDialogIsOpen, setVoucherCodeDialogIsOpen] =
    useState(false);
  const [t] = useTranslation();

  console.log("Voucher code", rebateLines);
  let content = (
    <li>
      <Box display="flex" justifyContent="space-between">
        <span>
          <Trans>Voucher code</Trans>
        </span>
        <button
          className={classes.addCodeButton}
          onClick={() => setVoucherCodeDialogIsOpen(true)}
        >
          <Trans>Add code</Trans>
        </button>
      </Box>
    </li>
  );

  // Happens if we used an rebate code but didn't get any rebate:
  if (rebateCode && rebateLines.length == 0) {
    rebateLines.push({
      description: t("Discount"),
      linePrice: 0,
    });
  }

  if (rebateLines.length > 0) {
    content = (
      <>
        {rebateLines.map((rebateLine, index) => {
          return (
            <Box key={index} display="flex" justifyContent="space-between">
              <span>{rebateLine.description}</span>
              <span className={classes.priceToPay}>
                {formatCurrency(rebateLine.linePrice)}
              </span>
            </Box>
          );
        })}
      </>
    );
  }

  return (
    <ul className={classes.orderContentList}>
      {content}
      {voucherCodeDialogIsOpen && (
        <VoucherCodeDialog
          handleClose={() => setVoucherCodeDialogIsOpen(false)}
        />
      )}
    </ul>
  );
};

const PackageInCart = ({ orderLine }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  console.log("Package in cart", orderLine);
  return (
    <>
      <Box display="flex" justifyContent="space-between">
        <div className={classes.orderLinePrimary}>
          {orderLine.quantity > 1
            ? t("%d pcs", {
                postProcess: "sprintf",
                sprintf: [orderLine.quantity],
              })
            : null}{" "}
          {orderLine.description}
        </div>
        <Box
          className={
            orderLine.rebate
              ? classes.priceBeforeRebate
              : classes.orderLinePrice
          }
        >
          {formatCurrency(orderLine.linePrice)}
        </Box>
      </Box>
      {orderLine.rebate && (
        <Box display="flex" justifyContent="space-between">
          <div>
            <Trans>Buy now price</Trans>
          </div>
          <Box className={classes.orderLinePrice}>
            {formatCurrency(orderLine.actualLinePrice)}
          </Box>
        </Box>
      )}
      <PackageItemList items={orderLine.package.packageItems} />
    </>
  );
};

const PackageItemList = ({ items }) => {
  const classes = useStyles();

  const products = items
    .reduce((products, packageItem) => {
      let product = products.find((p) => p.uuid == packageItem.product.uuid);
      if (!product) {
        product = {
          ...packageItem.product,
          packageItems: [],
        };
        products.push(product);
      }
      product.packageItems.push(packageItem);
      return products;
    }, [])
    .sort((a, b) => {
      return a.name.localeCompare(b.name);
    });

  return (
    <ul className={classes.packageProductList}>
      {products.map((product) => {
        return (
          <li key={product.uuid}>
            <div className={classes.packageProductName}>{product.name}</div>
            <ul>
              {product.packageItems.map((packageItem, index) => {
                let productDescription = "";
                if (packageItem.type == "DIGITAL_SET") {
                  productDescription = `${packageItem.subject.group}: ${packageItem.subject.name}`;
                } else if (packageItem.type == "CATALOGUE") {
                  productDescription = packageItem.catalogue.name;
                } else {
                  productDescription = packageItem.template.name;
                }
                return (
                  <li key={index}>
                    <div className={classes.packageTemplateName}>
                      {productDescription}
                    </div>
                  </li>
                );
              })}
            </ul>
          </li>
        );
      })}
    </ul>
  );
};

const PackageItemName = ({ packageItem }) => {
  switch (packageItem.type) {
    case "TEMPLATE_PRODUCT":
      return <TemplateName uuid={packageItem.template.uuid} />;
    case "DIGITAL_SET": {
      const { group, subject } = packageItem.subject;
      return (
        <>
          {group} {subject}
        </>
      );
    }
    case "CATALOGUE":
      return <>{packageItem.catalogue.name}</>;
  }
};

const TemplateName = ({ uuid }) => {
  const [template, isLoading] = useTemplate(uuid);
  if (isLoading) {
    return null;
  }
  console.log("Template is", template);
  return <>{getTemplateTranslation(template?.name, i18next.language)}</>;
};

const ProductInCart = ({ orderLine }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  let productTitle = orderLine.product.name;
  if (orderLine.product.uuid == PRODUCT_CATALOGUE_UUID) {
    productTitle = t("Catalogue");
  }

  console.log("Order line", orderLine);

  return (
    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
      <div>
        <div className={classes.orderLinePrimary}>
          {orderLine.quantity > 1
            ? t("%d pcs", {
                postProcess: "sprintf",
                sprintf: [orderLine.quantity],
              })
            : null}{" "}
          {productTitle}
        </div>
        <div className={classes.orderLineSecondary}>
          <SubProducts
            productUuid={orderLine.product.uuid}
            subProductUuids={orderLine.subProductUuids}
          />
          <OrderLineShortDescription orderLine={orderLine} />
        </div>
      </div>
      <Box className={classes.orderLinePrice}>
        <OrderLinePrice orderLine={orderLine} />
      </Box>
    </Box>
  );
};

const OrderLinePrice = ({ orderLine }) => {
  const classes = useStyles();

  if (orderLine.rebate) {
    return (
      <div>
        <div className={classes.oldPrice}>
          {formatCurrency(orderLine.linePrice)}
        </div>
        <div>{formatCurrency(orderLine.actualLinePrice)}</div>
      </div>
    );
  } else {
    return <div>{formatCurrency(orderLine.actualLinePrice)}</div>;
  }
};

const OrderLineShortDescription = ({ orderLine }) => {
  if (orderLine.template) {
    return <TemplateName uuid={orderLine.template.uuid} />;
  } else if (orderLine.catalogue) {
    return <>{orderLine.catalogue.name}</>;
  } else if (orderLine.product.uuid == PRODUCT_DIGITAL_SET_UUID) {
    return (
      <>
        {orderLine.subjects.map((subject) => (
          <>{subject.name}</>
        ))}
      </>
    );
  }
  return null;
};

const DisplayOrderSummary = ({ orderLine }) => {
  const classes = useStyles();
  return (
    <Box display="flex" justifyContent="space-between">
      <div>{orderLine.description}</div>
      <div className={classes.priceToPay}>
        {formatCurrency(orderLine.linePrice)}
      </div>
    </Box>
  );
};

const SubProducts = ({ productUuid, subProductUuids }) => {
  const { getProductByUuid } = usePriceList();
  const classes = useStyles();

  const product = useMemo(() => {
    return getProductByUuid(productUuid);
  }, [subProductUuids]);

  if (subProductUuids.length == 0) {
    return null;
  }

  let nameParts = [];
  for (const uuid of subProductUuids) {
    for (const subProduct of product.subproducts) {
      if (subProduct.uuid === uuid) {
        nameParts.push(subProduct.name);
      }
    }
  }

  return <div className={classes.subProductName}>{nameParts.join(" • ")}</div>;
};

export default OrderContent;
